import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useId, useState } from 'react'
import { CompanyDot, directoryQueryOptions, findCompany, findProject } from '@/entities/directory'
import { type Employee, employeesQueryOptions } from '@/entities/employee'
import {
  type AttendanceEntry,
  type CalendarEvent,
  callStateLabels,
  dealOutcomeLabels,
  type EventGuest,
  type EventParticipant,
  EventSourceBadge,
  eventDisplayTitle,
  eventLinkLabel,
  eventQueryOptions,
  eventSourceLabel,
  eventStateLabels,
  eventTypeDotClass,
  eventTypeLabels,
  hasAttendance,
  isExternalEvent,
  isPastEvent,
  participantRoleLabels,
  seriesPositionLabel,
  seriesRuleLabel,
  showsPrivateMark,
  sortedEventLinks,
  splitAttendance,
  taskKindLabels,
} from '@/entities/event'
import { isCustomerSession, useSession } from '@/entities/session'
import { EntityTagsEditor, entityTagsQueryOptions } from '@/entities/tag'
import { CancelEventDialog } from '@/features/cancel-event'
import { DeleteEventDialog } from '@/features/delete-event'
import { EditEventDialog } from '@/features/edit-event'
import { CustomerVisibilityControl } from '@/features/set-customer-visibility'
import { ApiError } from '@/shared/api'
import {
  APP_TIME_ZONE,
  cn,
  fmtDuration,
  fmtEventRange,
  fmtFullDate,
  fmtTime,
  fromISODate,
  momentZone,
} from '@/shared/lib'
import { Avatar } from '@/shared/ui/avatar'
import { Button } from '@/shared/ui/button'
import { Card } from '@/shared/ui/card'
import { Chip } from '@/shared/ui/chip'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneTag } from '@/shared/ui/zone-tag'

export const Route = createFileRoute('/_app/events/$eventId')({
  component: EventPage,
})

// Переход наружу и в источник — <a>, а не кнопка (средний клик, «копировать
// адрес»). Рецепт тот же, что у Button secondary/sm.
const ACTION_LINK =
  'tap-target inline-flex items-center justify-center gap-2 rounded-control border border-border-strong px-3 pt-[9px] pb-[7px] font-medium font-mono text-text-primary text-xs uppercase tracking-wide transition-colors hover:border-text-primary hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent'

function EventPage() {
  const { eventId } = Route.useParams()
  const navigate = Route.useNavigate()
  const event = useQuery(eventQueryOptions(eventId))

  if (event.isPending) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <Spinner label="Загружаем событие" />
      </div>
    )
  }

  if (event.isError) {
    const notFound = event.error instanceof ApiError && event.error.status === 404
    return (
      <div className="px-gutter py-8">
        <EmptyState
          title={notFound ? 'Событие не найдено' : 'Не удалось загрузить событие'}
          description={
            notFound
              ? 'Его удалили — или ссылка ведёт в никуда.'
              : 'Проверьте сеть и попробуйте ещё раз.'
          }
          action={
            notFound ? (
              <Link to="/calendar" className={ACTION_LINK}>
                К календарю
              </Link>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => void event.refetch()}>
                Повторить
              </Button>
            )
          }
        />
      </div>
    )
  }

  return <EventDetails event={event.data} onGone={() => void navigate({ to: '/calendar' })} />
}

function EventDetails({ event, onGone }: { event: CalendarEvent; onGone: () => void }) {
  const [editing, setEditing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  // Заказчику календарь — витрина (00-architecture § 6): ни действий, ни
  // студийного контура (справочник, сотрудники, теги). Справочники ему не
  // адресованы, поэтому запросы за ними даже не уходят.
  const session = useSession()
  const customer = isCustomerSession(session)
  const { data: companies = [] } = useQuery({ ...directoryQueryOptions(), enabled: !customer })
  const { data: employees = [] } = useQuery({ ...employeesQueryOptions, enabled: !customer })

  const external = isExternalEvent(event)
  // Присутствие есть о чём показывать только когда источник о нём сказал: у
  // события без звонка и у скрытого деталями состав остаётся одним списком.
  const attendanceKnown = !event.redacted && hasAttendance(event.roles, event.guests)
  // Ядро внешнего события правит источник, прошлое неизменяемо (policy бэка), а
  // у скрытого деталями править нечего — во всех трёх случаях действий нет.
  const editable = !customer && !external && !event.redacted && !isPastEvent(event)
  const company = findCompany(companies, event.companyId)
  const project = findProject(companies, event.projectId)
  const author = employees.find((e) => e.id === event.authorId)
  const links = sortedEventLinks(event.links)
  const primaryLink = links[0]

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        eyebrow="Событие"
        title={eventDisplayTitle(event)}
        status={
          <div className="flex flex-wrap items-center gap-2">
            <Chip>
              <span
                aria-hidden="true"
                className={cn('size-1.5 rounded-pill', eventTypeDotClass[event.type])}
              />
              {eventTypeLabels[event.type]}
            </Chip>
            {event.state !== 'active' && (
              <Chip tone={event.state === 'cancelled' ? 'accent' : 'success'}>
                {eventStateLabels[event.state]}
              </Chip>
            )}
            <EventSourceBadge source={event.source} />
            {/* Приватность называем только своему кругу: у постороннего событие
                уже `redacted`, и маркер выдал бы ровно то, что прячется. */}
            {showsPrivateMark(event) && (
              <Chip tone="warning">
                <Icon name="eye-off" size={12} />
                Приватное
              </Chip>
            )}
          </div>
        }
        actions={
          <>
            {editable && (
              <>
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                  <Icon name="pencil" size={14} />
                  Изменить
                </Button>
                {/* Отмена — не удаление: событие остаётся в календаре
                    зачёркнутым, и обратное действие рядом. */}
                <Button variant="ghost" size="sm" onClick={() => setCancelling(true)}>
                  {event.state === 'cancelled' ? 'Вернуть' : 'Отменить'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleting(true)}>
                  <Icon name="close" size={14} />
                  Удалить
                </Button>
              </>
            )}
            {external && primaryLink && (
              <a href={primaryLink[1]} target="_blank" rel="noreferrer" className={ACTION_LINK}>
                <Icon name="external-link" size={14} />
                {eventLinkLabel(primaryLink[0])}
              </a>
            )}
          </>
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-gutter pb-safe-8">
        <div className="flex max-w-3xl flex-col gap-4">
          <Card>
            <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[9rem_1fr]">
              <Row label="Когда">
                <span className="mono-num first-letter:uppercase">{fmtEventRange(event)}</span>
                <span className="text-text-tertiary"> · {fmtDuration(event)}</span>
                {/* Момент подписан тегом зоны, сутки — нет: у «весь день» нет
                    времени суток, и тег читался бы как момент. Но границы этих
                    суток зона всё равно задаёт — про это сказано словами. */}
                {event.allDay ? (
                  <span className="mt-0.5 block text-text-tertiary text-xs">
                    Сутки считаются в зоне организации ({APP_TIME_ZONE})
                  </span>
                ) : (
                  <ZoneTag className="ml-1.5 text-xs" />
                )}
              </Row>
              {company && (
                <Row label="Клиент">
                  <Link
                    to="/companies/$companyId"
                    params={{ companyId: company.id }}
                    className="inline-flex items-center gap-1.5 transition-colors hover:text-text-primary"
                  >
                    <CompanyDot color={company.color} />
                    {company.name}
                  </Link>
                </Row>
              )}
              {project && (
                <Row label="Проект">
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: project.id }}
                    className="transition-colors hover:text-text-primary"
                  >
                    {project.name}
                  </Link>
                </Row>
              )}
              {event.series && (
                // Конец серии приезжает календарным днём (колонка DATE у бэка) —
                // тегом зоны он не подписывается, но сказать, что времени суток
                // у него нет, стоит: иначе «до 31 декабря» читается моментом.
                <Row label="Повторение" title="Конец серии — календарный день, без времени суток">
                  {seriesRuleLabel(event.series)}
                  {seriesPositionLabel(event.series) && (
                    <span className="text-text-tertiary">
                      {' '}
                      · {seriesPositionLabel(event.series)}
                    </span>
                  )}
                </Row>
              )}
              {external && (
                <Row label="Источник">
                  {eventSourceLabel(event.source)}
                  <span className="text-text-tertiary">
                    {' '}
                    · правится там, здесь только показывается
                  </span>
                </Row>
              )}
            </dl>
          </Card>

          {event.redacted ? (
            <Card>
              <div className="flex flex-col gap-2">
                <h2 className="t-h3">Детали скрыты</h2>
                <p className="text-sm text-text-secondary">
                  Событие приватное, и вы не входите в круг его участников. Видно только, что время
                  занято: названия, описания и ссылок здесь нет — они не потерялись при загрузке.
                </p>
              </div>
            </Card>
          ) : (
            <>
              {event.description && (
                <Card>
                  <div className="flex flex-col gap-2">
                    <span className="label">Описание</span>
                    <p className="whitespace-pre-wrap break-words text-sm text-text-secondary">
                      {event.description}
                    </p>
                  </div>
                </Card>
              )}

              {event.meeting && (
                <Card>
                  <div className="flex flex-col gap-3">
                    <span className="label">Встреча</span>
                    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[9rem_1fr]">
                      <Row label="Звонок">{callStateLabels[event.meeting.callState]}</Row>
                      {event.meeting.startedAt && (
                        <Row label="Начался">
                          <Moment at={event.meeting.startedAt} />
                        </Row>
                      )}
                      {event.meeting.endedAt && (
                        <Row label="Завершился">
                          <Moment at={event.meeting.endedAt} />
                        </Row>
                      )}
                      {event.meeting.organizerName && (
                        <Row label="Организатор">{event.meeting.organizerName}</Row>
                      )}
                      {event.meeting.private && (
                        <Row label="Доступ">Приватная — детали видит только круг участников</Row>
                      )}
                    </dl>
                  </div>
                </Card>
              )}

              {/* Внутренняя кухня источника (доска, колонка, этап воронки) —
                  студийная: заказчику показываем только сам факт и время. */}
              {event.task && !customer && (
                <Card>
                  <div className="flex flex-col gap-3">
                    <span className="label">Задача</span>
                    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[9rem_1fr]">
                      <Row label="Вид">{taskKindLabels[event.task.kind]}</Row>
                      {event.task.boardName && <Row label="Доска">{event.task.boardName}</Row>}
                      {event.task.columnName && (
                        <Row label="Колонка">
                          {event.task.columnName}
                          {event.task.done && (
                            <span className="text-text-tertiary"> · выполнено</span>
                          )}
                        </Row>
                      )}
                      <Row label="Исполнитель">
                        <Assignee roles={event.roles} employees={employees} />
                      </Row>
                      {event.task.priority !== null && (
                        <Row label="Приоритет">{event.task.priority}</Row>
                      )}
                      {event.task.archived && (
                        <Row label="Архив">Задача в архиве — из сетки календаря она убрана</Row>
                      )}
                    </dl>
                  </div>
                </Card>
              )}

              {event.deal && !customer && <DealCard event={event} employees={employees} />}

              {links.length > 0 && (
                <Card>
                  <div className="flex flex-col gap-3">
                    <span className="label">{external ? 'Ссылки в источник' : 'Ссылки'}</span>
                    <div className="flex flex-wrap gap-2">
                      {links.map(([key, url]) => (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className={ACTION_LINK}
                        >
                          <Icon name="external-link" size={14} />
                          {eventLinkLabel(key)}
                        </a>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          {/* Заказчику состав приезжает готовыми ФИО (customer-DTO бэка): ни
              справочника сотрудников, ни их страниц у него нет, поэтому имена —
              просто текст. */}
          {customer && event.participantNames.length > 0 && (
            <Card>
              <div className="flex flex-col gap-3">
                <span className="label">Участники</span>
                <ul className="flex flex-col gap-2">
                  {event.participantNames.map((name) => (
                    <li key={name} className="flex items-center gap-2 text-sm text-text-secondary">
                      <Avatar name={name} size="sm" />
                      <span className="truncate">{name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          )}

          {/* Состав со стороны студии — внутренний контур: id, роли и страницы
              сотрудников остаются у сотрудников. */}
          {!customer && (
            <Card>
              <div className="flex flex-col gap-3">
                <span className="label">Участники</span>
                {attendanceKnown ? (
                  <Attendance roles={event.roles} guests={event.guests} employees={employees} />
                ) : (
                  <>
                    <Participants roles={event.roles} employees={employees} />
                    {!event.redacted && event.guests.length > 0 && (
                      <div className="flex flex-col gap-2 border-border-default border-t pt-3">
                        <span className="label text-[10px]">Гости без аккаунта</span>
                        <ul className="flex flex-col gap-1">
                          {event.guests.map((guest) => (
                            <li
                              key={`${guest.name}:${guest.contact}`}
                              className="flex items-center gap-2 text-sm text-text-secondary"
                            >
                              <Avatar name={guest.name} size="sm" />
                              <span className="truncate">{guest.name}</span>
                              <span className="text-text-tertiary">
                                · {participantRoleLabels[guest.role]}
                              </span>
                              {guest.contact && (
                                <span className="truncate text-text-tertiary text-xs">
                                  {guest.contact}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          )}

          {/* Пометка «видно заказчику» (00-architecture § 4) — сотруднический
              контроль: заказчик её не переключает и не видит. */}
          {!customer && !event.redacted && (
            <Card>
              <div className="flex flex-col gap-3">
                <span className="label">Заказчик</span>
                <CustomerVisibilityControl event={event} />
              </div>
            </Card>
          )}

          {!customer && !event.redacted && (
            <Card>
              <div className="flex flex-col gap-3">
                <span className="label">Теги</span>
                {/* Теги — локальный контур календаря: правятся и у внешнего события. */}
                <EventTags eventId={event.id} />
              </div>
            </Card>
          )}

          {!customer && (
            <dl className="grid gap-x-6 gap-y-2 px-1 text-text-tertiary text-xs sm:grid-cols-[9rem_1fr]">
              {author && <Row label="Автор">{author.displayName}</Row>}
              {event.createdAt && (
                <Row label="Создано">
                  <Moment at={event.createdAt} />
                </Row>
              )}
              {event.updatedAt && (
                <Row label="Обновлено">
                  <Moment at={event.updatedAt} />
                </Row>
              )}
            </dl>
          )}
        </div>
      </div>

      {editing && <EditEventDialog event={event} onClose={() => setEditing(false)} />}
      <CancelEventDialog event={cancelling ? event : null} onClose={() => setCancelling(false)} />
      <DeleteEventDialog
        event={deleting ? event : null}
        onClose={() => setDeleting(false)}
        onDeleted={onGone}
      />
    </div>
  )
}

function Row({
  label,
  title,
  children,
}: {
  label: string
  /** Пояснение к значению — например, что это календарный день, а не момент. */
  title?: string
  children: ReactNode
}) {
  return (
    <>
      <dt className="label self-baseline">{label}</dt>
      <dd className="min-w-0 text-sm text-text-secondary" {...(title ? { title } : {})}>
        {children}
      </dd>
    </>
  )
}

/**
 * Момент вне календарной сетки (звонок, аудит): значение + короткий тег зоны.
 * Сетки с подписанной осью здесь нет, и без тега время читалось бы по
 * устройству — а карточку открывают в том числе из ленты и из поиска.
 */
function Moment({ at }: { at: Date }) {
  return (
    <>
      <span className="mono-num">{fmtDateTime(at)}</span>
      <ZoneTag className="ml-1.5 text-xs" />
    </>
  )
}

// Исполнитель — единственный участник задачи; он же тот, у кого она в личном
// календаре. Пусто — задача не назначена и ничей календарь не занимает.
/**
 * Исполнитель: сотрудник ссылкой на его страницу. Задачу сделки исполняет
 * человек заказчика — аккаунта в хабе у него нет, и он приезжает гостем с той же
 * ролью.
 */
function Assignee({
  roles,
  employees,
  guests = [],
}: {
  roles: EventParticipant[]
  employees: Employee[]
  guests?: EventGuest[]
}) {
  const assignee = roles.find((r) => r.role === 'assignee')
  const employee = assignee && employees.find((e) => e.id === assignee.employeeId)
  if (!employee) {
    const guest = guests.find((g) => g.role === 'assignee')
    if (guest) {
      return (
        <span className="inline-flex min-w-0 items-center gap-2">
          <Avatar name={guest.name} size="sm" />
          <span className="truncate">{guest.name}</span>
          <span className="text-text-tertiary text-xs">· без аккаунта</span>
        </span>
      )
    }
    return <span className="text-text-tertiary">Не назначен</span>
  }
  return (
    <Link
      to="/employees/$employeeId"
      params={{ employeeId: employee.id }}
      className="inline-flex min-w-0 items-center gap-2 transition-opacity hover:opacity-80"
    >
      <Avatar name={employee.displayName} size="sm" />
      <span className="truncate">{employee.displayName}</span>
    </Link>
  )
}

/**
 * «Кто был» и «кого звали» — два разных факта, и карточка разводит их явно:
 * присутствие приезжает из фида телемоста признаком поверх роли, а не вместо
 * неё. Времён входа и длительности в контракте нет — только список.
 */
function Attendance({
  roles,
  guests,
  employees,
}: {
  roles: EventParticipant[]
  guests: EventGuest[]
  employees: Employee[]
}) {
  const { attended, invited } = splitAttendance(roles, guests)
  return (
    <div className="flex flex-col gap-3">
      <PeopleGroup label="Был на звонке" people={attended} employees={employees} />
      {invited.length > 0 && (
        <div className="border-border-default border-t pt-3">
          <PeopleGroup label="Звали, не заходил" people={invited} employees={employees} />
        </div>
      )}
    </div>
  )
}

// Список подписан своим заголовком (aria-labelledby): «был» и «звали» —
// одинаковые с виду перечни, и без имени они неразличимы для скринридера.
function PeopleGroup({
  label,
  people,
  employees,
}: {
  label: string
  people: AttendanceEntry[]
  employees: Employee[]
}) {
  const labelId = useId()
  return (
    <div className="flex flex-col gap-2">
      <span className="label text-[10px]" id={labelId}>
        {label}
      </span>
      <ul aria-labelledby={labelId} className="flex flex-col gap-1">
        {people.map((person) => (
          <PersonRow key={person.key} person={person} employees={employees} />
        ))}
      </ul>
    </div>
  )
}

/**
 * Человек состава. Сотрудник — ссылкой на свою страницу; тот, кого справочник
 * ещё не знает (фактический участник приезжает раньше pull-синка хаба), —
 * заглушкой с пометкой: молча исчезнуть из состава он не должен. Человек без
 * аккаунта адресуется именем из факта — страницы у него нет вовсе.
 */
function PersonRow({ person, employees }: { person: AttendanceEntry; employees: Employee[] }) {
  const employee = person.guest ? undefined : employees.find((e) => e.id === person.employeeId)
  const role = <span className="text-text-tertiary">· {participantRoleLabels[person.role]}</span>

  if (employee) {
    return (
      <li className="flex items-center gap-2 text-sm text-text-secondary">
        <Link
          to="/employees/$employeeId"
          params={{ employeeId: employee.id }}
          className="flex min-w-0 items-center gap-2 transition-opacity hover:opacity-80"
        >
          <Avatar name={employee.displayName} size="sm" />
          <span className="truncate">{employee.displayName}</span>
        </Link>
        {role}
      </li>
    )
  }

  const name = person.guest ? person.name : 'Участник вне справочника'
  return (
    <li className="flex items-center gap-2 text-sm text-text-secondary">
      <Avatar name={name} size="sm" />
      <span className="truncate">{name}</span>
      {role}
      <span className="truncate text-text-tertiary text-xs">
        · {person.guest ? 'без аккаунта' : 'ещё не в справочнике'}
      </span>
    </li>
  )
}

function Participants({ roles, employees }: { roles: EventParticipant[]; employees: Employee[] }) {
  const known = roles
    .map((role) => ({ role, employee: employees.find((e) => e.id === role.employeeId) }))
    .filter((p): p is { role: EventParticipant; employee: Employee } => p.employee !== undefined)

  if (known.length === 0) {
    return <p className="text-sm text-text-tertiary">Участников нет</p>
  }

  return (
    <ul className="flex flex-col gap-2">
      {known.map(({ role, employee }) => (
        <li key={employee.id} className="flex items-center gap-2 text-sm text-text-secondary">
          <Link
            to="/employees/$employeeId"
            params={{ employeeId: employee.id }}
            className="flex min-w-0 items-center gap-2 transition-opacity hover:opacity-80"
          >
            <Avatar name={employee.displayName} size="sm" />
            <span className="truncate">{employee.displayName}</span>
          </Link>
          <span className="text-text-tertiary">· {participantRoleLabels[role.role]}</span>
        </li>
      ))}
    </ul>
  )
}

function EventTags({ eventId }: { eventId: string }) {
  const tags = useQuery(entityTagsQueryOptions('event', eventId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return <EntityTagsEditor entityKind="event" entityId={eventId} tags={tags.data ?? []} />
}

/**
 * `20 июля 2026 · 14:30` — отметки времени вне календарной сетки. Дата и время
 * одного момента читаются в одной зоне — зоне устройства: половины в разных
 * зонах разъезжаются на сутки на границе полуночи.
 */
function fmtDateTime(d: Date): string {
  const tz = momentZone()
  return `${fmtFullDate(d, tz)} · ${fmtTime(d, tz)}`
}

/**
 * Дата без времени суток (старт сделки) приезжает строкой `YYYY-MM-DD`: момент
 * из неё собирается полуночью зоны организации — только чтобы отформатировать.
 */
function fmtDayString(day: string): string {
  const d = fromISODate(day)
  return d ? fmtFullDate(d) : day
}

/**
 * Детали источника stit-deal. Расширение одно на три вида сущности, поэтому
 * показываем то, что заполнено: у сделки — этап, ответственный, исход и старт, у
 * задачи сделки — исполнитель, у очной встречи — место.
 */
function DealCard({ event, employees }: { event: CalendarEvent; employees: Employee[] }) {
  const deal = event.deal
  if (!deal) return null
  const isOnsite = event.externalKind === 'onsite'
  const isTask = event.externalKind === 'task'
  const label = isOnsite ? 'Очная встреча' : isTask ? 'Задача сделки' : 'Сделка'
  return (
    <Card>
      <div className="flex flex-col gap-3">
        <span className="label">{label}</span>
        <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[9rem_1fr]">
          {deal.location && <Row label="Место">{deal.location}</Row>}
          {deal.stageName && (
            <Row label="Этап">
              {deal.stageName}
              {deal.boardName && <span className="text-text-tertiary"> · {deal.boardName}</span>}
            </Row>
          )}
          {deal.responsibleName && <Row label="Ответственный">{deal.responsibleName}</Row>}
          {isTask && (
            <Row label="Исполнитель">
              <Assignee roles={event.roles} employees={employees} guests={event.guests} />
            </Row>
          )}
          {/* Старт сделки — колонка DATE источника: день без времени суток, и
              тегом зоны он не подписывается (иначе читался бы моментом). */}
          {deal.startDate && (
            <Row label="Старт" title="Календарный день источника, без времени суток">
              {fmtDayString(deal.startDate)}
            </Row>
          )}
          {deal.priority !== null && <Row label="Приоритет">{deal.priority}</Row>}
          {deal.outcome && <Row label="Итог">{dealOutcomeLabels[deal.outcome]}</Row>}
          {deal.archived && (
            <Row label="Архив">Сделка в архиве — из сетки календаря она убрана</Row>
          )}
        </dl>
      </div>
    </Card>
  )
}
